import { CellClassParams, CellEditorSelectorResult, CellStyle, ColDef, ColGroupDef, EditableCallbackParams, ICellEditorParams, ICellRendererParams } from "ag-grid-community";
import { format } from "date-fns";
import Enumerable from "linq";
import { GridColumnAggregateEnum } from "../../common/enums/GridColumnAggregateEnum";
import { ManualEntryFeatureEnum } from "../../common/enums/ManualEntryFeatureEnum";
import { NumericalFunctionEnum } from "../../common/enums/NumericalFunctionEnum";
import { SetPointEventTypeEnum } from "../../common/enums/SetPointEventTypeEnum";
import { ILookupListText } from "../../common/models/ILookupList";
import { ITagAssociationType } from "../../common/models/ITagAssociationType";
import { ITagModel } from "../../common/models/ITagModel";
import { getAggregateFunction } from "../../components/ag-grid-extensions/AggregateHelper";
import { INumericCellEditorProps } from "../../components/ag-grid-extensions/cell-editors/NumericCellEditor";
import { IGroupManualEntryModel } from "../../services/manual-entry-data-service/Models";
import { CustomCellEditorProps } from "ag-grid-react";
import AggregateCellRenderer from "../../components/ag-grid-extensions/renderers/AggregateCellRenderer";

const MANUAL_ENTRY_BACKGROUND_COLOR = "rgb(211, 211, 211, .2)";
const CALCULATED_BACKGROUND_COLOR = "rgb(41, 57, 85, .2)";

export type RowData = {
    deviceName: string,
    deviceID: number,
    deviceTypeID: number,
    deviceSubTypeID: number,
    features: number | undefined,
    //memo: IMemoModel | undefined;
    order: number | undefined;
    memoID: number | undefined;
    memoText: string | undefined;
    tags: Array<ITagModel>;
    [key: `tat${number}`]: string | number;
};

export function createGridColumns(
    date: Date,
    data: IGroupManualEntryModel,
    isPhone: boolean
): Array<ColDef | ColGroupDef> {

    const tagColDefs = data.tagAssociationTypes.map(tat => {

        const allTags = Enumerable
            .from(data.devices)
            .selectMany(o => o.tags)
            .where(o => o.tatID === tat.id)
            .toArray();

        const unitName = Enumerable
            .from(allTags)
            .firstOrDefault(o => (o.uomID ?? 0) > 0)?.unitDisplayName;

        let headerName = Enumerable
            .from(allTags)
            .firstOrDefault()?.displayText ?? tat.name;

        if (unitName) {
            headerName = `${headerName} (${unitName})`;
        }

        let width: number = 160;

        const listID = Enumerable
            .from(allTags)
            .firstOrDefault(o => (o?.listID ?? 0) > 0)?.listID;

        if (listID) {
            const lookupList = Enumerable
                .from(data.tagLookupList)
                .firstOrDefault(o => o.id === listID);

            if (lookupList) {
                Enumerable.from(lookupList.listText)
                    .where(o => o.description === undefined)
                    .forEach(function (listText: ILookupListText) {
                        listText.description = " ";
                    });

                // Get the longest list text item and set the width
                var len: number = Enumerable.from(lookupList.listText).max(o => o.description.length);

                if ((len * 10) > width) {
                    width = (len * 10);
                }
            }
        }

        const column: ColDef = createTagColummn(headerName, tat, width, isPhone);
        return column;
    }) ?? [];

    const objectColDef: ColDef = {
        headerName: "Device Name",
        field: "deviceName",
        editable: false,
        pinned: !isPhone,
        cellStyle: {
            "cursor": "pointer",
            //"max-width": isPhone ? "230px" : '',
        },
        aggFunc: getAggregateFunction(GridColumnAggregateEnum.Count),
        cellRendererSelector: (params: ICellRendererParams) => {
            if (params.node.group || params.node.rowPinned === "bottom") {
                return {
                    component: AggregateCellRenderer
                };
            } else {
                // rows that are not a footer don't use any cell renderer
                return undefined;
            }
        }
    };

    const memoColDef: ColDef = {
        headerName: "Memo",
        field: "memoText",
        //flex: 2, // => Bug 51745: Tag Values Grid Autosize Columns
        editable: (params: EditableCallbackParams) => {
            const features = params.data.features;
            return (features & ManualEntryFeatureEnum.MemoModify) === ManualEntryFeatureEnum.MemoModify;
        },
        cellEditorPopup: true,
        cellEditor: "agLargeTextCellEditor",
        cellEditorParams: (params: ICellEditorParams) => {
            return {
                ...params,
                maxLength: 8000,
                rows: 4,
                cols: 40
            };
        }
    };

    const columnGroups = [{
        headerName: format(date ?? new Date(), 'LLLL dd, yyyy'),
        headerClass: "elynx-group-manual-entry",
        children: [
            objectColDef
        ]
    },{
        headerName: "",
        children: [
            ...tagColDefs,
            memoColDef
        ]
    }]

    return columnGroups;
}

function createTagColummn(headerName: string, tat: ITagAssociationType, width: number, isPhone: boolean) {
    const field = `tat${tat.id}`;
    const footerAggregate = tat.summaryFunction === NumericalFunctionEnum.Sum
        ? GridColumnAggregateEnum.Sum
        : tat.summaryFunction === NumericalFunctionEnum.Average
            ? GridColumnAggregateEnum.Average
            : GridColumnAggregateEnum.None;

    const column: ColDef = {
        headerName: headerName,
        field: field,
        minWidth: width,
        //flex: 1, // => Bug 51745: Tag Values Grid Autosize Columns
        editable: (params: EditableCallbackParams) => {
            if (isPhone) {
                return false;
            }
            const tag = _getTag(params, tat);
            return ((tag?.features ?? ManualEntryFeatureEnum.None) & ManualEntryFeatureEnum.ManualEntry) === ManualEntryFeatureEnum.ManualEntry;
        },
        cellStyle: (params: CellClassParams) => {
            const tag = _getTag(params, tat);
            const cellStyle: CellStyle = {
                borderColor: "",
                backgroundColor: tag?.tagValue?.setPointEventType === SetPointEventTypeEnum.Manual
                    ? MANUAL_ENTRY_BACKGROUND_COLOR
                    : tag?.tagValue?.setPointEventType === SetPointEventTypeEnum.Calculated
                        ? CALCULATED_BACKGROUND_COLOR
                        : ""
            };
            return cellStyle;
        },
        cellEditorSelector: (params: ICellRendererParams | ICellEditorParams): CellEditorSelectorResult | undefined => {
            const tag = _getTag(params, tat);
            if (tag?.tatID) {
                if (tag?.lookupList) {
                    return {
                        component: 'agRichSelectCellEditor',
                        popup: true,
                        params: {
                            values: ['', ...tag.lookupList?.listText.map((o) => o.description)]
                        }
                    };
                } else {
                    return {
                        component: 'numericCellEditor'
                    };
                }
            }
        },
        //cellEditorParams: (params: ICellEditorParams) => {
        cellEditorParams: (params: CustomCellEditorProps) => {
            const tag = _getTag(params, tat);

            if (!tag?.listID) {
                const cellEditorParams: INumericCellEditorProps = {
                    ...params,
                    decimals: tag?.decimalPlaces,
                    lowerBound: tag?.lowerBound,
                    lowerBoundOp: tag?.lowerBoundOp,
                    upperBound: tag?.upperBound,
                    upperBoundOp: tag?.upperBoundOp,
                };

                return cellEditorParams;
            } else {
                return params;
            }
        },
        cellRendererSelector: (params: ICellRendererParams) => {
            if (params.node.group || params.node.rowPinned === "bottom") {
                return {
                    component: AggregateCellRenderer
                };
            } else {
                // rows that are not a footer don't use any cell renderer
                return undefined;
            }
        },
        aggFunc: getAggregateFunction(footerAggregate),
    };
    return column;
}

export function createGridRows(
    data: IGroupManualEntryModel
): Array<RowData> {

    const rows = Enumerable
        .from(data.devices)
        .select(device => {
            const tatTags = Enumerable
                .from(data.tagAssociationTypes)
                .leftJoin(
                    device.tags,
                    o => o.id,
                    i => i.tatID,
                    (o,i) => ({i, o})
                )
                .toArray();

            const tagCells = tatTags.reduce<{[key: string]: any}>((previous, current) => {
                previous[`tat${current.o.id}`] = current.i?.tagValue?.displayText ?? ""
                return previous;
            }, {});

            let row: RowData = {
                deviceName: device.displayText,
                deviceID: device.id,
                deviceTypeID: device.deviceTypeID,
                deviceSubTypeID: device.deviceSubTypeID,
                features: device.features,
                //memo: device.memo,
                memoID: device.memo?.memoID,
                memoText: device.memo?.text,
                order: device.order,
                tags: tatTags.filter(oi => oi.i !== null).map(oi => {
                    return {
                        ...oi.i,
                        lookupList: data.tagLookupList.find(l => l.id === oi.i.listID)
                    };
                }),
                ...tagCells,
            };

        return row;
    })
    //.orderBy(o => o.deviceName.toLowerCase())
    .orderBy(o => o.order)
    .toArray();

    return rows;
}

function _getTag(
        params: {data: RowData},
    tat: ITagAssociationType
) {
    const tags: ITagModel[] = params.data?.tags ?? [];
    const tag = tags.find(v => v.tatID === tat.id);
    return tag;
}